import * as React from 'react';
import styled from 'styled-components';

import DashboardLayout from '../layouts/DashboardLayout';
import { getConfirmationStatus, EboxConfirmationStatus } from '../api/publicEbox';

type LookupState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'found'; data: EboxConfirmationStatus }
  | { kind: 'notFound'; confirmationNumber: string }
  | { kind: 'error'; message: string };

const ConfirmationStatusPage: React.FC = () => {
  const [input, setInput] = React.useState('');
  const [state, setState] = React.useState<LookupState>({
    kind: 'idle',
  });

  const lookup = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const confirmationNumber = input.trim();
    if (!confirmationNumber) return;

    setState({ kind: 'loading' });

    try {
      const { data } = await getConfirmationStatus(confirmationNumber);
      setState({ kind: 'found', data });
    } catch (err: any) {
      const status = err?.response?.status;

      if (status === 404) {
        setState({ kind: 'notFound', confirmationNumber });
      } else if (status === 400) {
        setState({
          kind: 'error',
          message: 'A confirmation number is required.',
        });
      } else {
        setState({
          kind: 'error',
          message: 'Could not reach the confirmation-status service. Please try again.',
        });
      }
    }
  };

  return (
    <DashboardLayout>
      <Page>
        <Card>
          <Title>EBox Confirmation Status</Title>
          <Subtitle>Look up an EBox confirmation number to see whether it is available or already in use.</Subtitle>

          <Form onSubmit={lookup}>
            <Field
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="e.g. EBOX-0326-NUC3X4"
              aria-label="Confirmation number"
              autoFocus
            />
            <PrimaryButton type="submit" disabled={state.kind === 'loading' || !input.trim()}>
              {state.kind === 'loading' ? 'Checking…' : 'Check'}
            </PrimaryButton>
          </Form>

          <Result state={state} />
        </Card>
      </Page>
    </DashboardLayout>
  );
};

const Result: React.FC<{ state: LookupState }> = ({ state }) => {
  if (state.kind === 'idle' || state.kind === 'loading') {
    return null;
  }

  if (state.kind === 'error') {
    return <Callout tone="danger">{state.message}</Callout>;
  }

  if (state.kind === 'notFound') {
    return (
      <Callout tone="neutral">
        No record found for <strong>{state.confirmationNumber}</strong>. This confirmation number is unknown.
      </Callout>
    );
  }

  const { data } = state;
  const isAvailable = data.Status === 'Available';
  const showDetails = data.Status === 'Not Available' && data.Live && !!data.Address;

  return (
    <ResultCard>
      <StatusRow>
        <StatusChip $available={isAvailable}>{data.Status}</StatusChip>
        <LiveBadge $live={data.Live}>{data.Live ? 'Live' : 'Not live'}</LiveBadge>
      </StatusRow>

      <FieldRow>
        <Label>Confirmation number</Label>
        <Value>{data.ConfirmationNumber}</Value>
      </FieldRow>

      {data.PublicUrl && data.PublicUrl.startsWith('https://') ? (
        <FieldRow>
          <Label>Public URL</Label>
          <Value>
            <Anchor href={data.PublicUrl} target="_blank" rel="noreferrer">
              {data.PublicUrl}
            </Anchor>
          </Value>
        </FieldRow>
      ) : null}

      {data.ProjectName ? (
        <FieldRow>
          <Label>Project name</Label>
          <Value>{data.ProjectName}</Value>
        </FieldRow>
      ) : null}

      {showDetails && data.Address ? (
        <FieldRow>
          <Label>Address</Label>
          <Value>
            {[
              data.Address.BusinessNameOrFullName,
              data.Address.FullAddress ||
                [data.Address.Street, data.Address.Street2, data.Address.City, data.Address.State, data.Address.ZipCode]
                  .filter(Boolean)
                  .join(', '),
            ]
              .filter(Boolean)
              .map((line, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i}>{line}</div>
              ))}
          </Value>
        </FieldRow>
      ) : null}
    </ResultCard>
  );
};

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont,
  'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji',
  'Segoe UI Emoji', 'Segoe UI Symbol'`;

const Page = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 20px 16px 0;
  font-family: ${FONT_STACK};

  @media only screen and (max-width: 840px) {
    padding: 12px 12px 0;
  }
`;

const Card = styled.div`
  max-width: 720px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 24px;
  margin-bottom: 20px;
  box-sizing: border-box;
`;

const Title = styled.h1`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  margin: 0 0 6px;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: var(--neutral-500);
  margin: 0 0 20px;
`;

const Form = styled.form`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
`;

const Field = styled.input`
  flex: 1;
  min-width: 240px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-size: 0.875rem;
  color: var(--mainText);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: var(--neutral-400);
  }
  &:hover {
    border-color: var(--neutral-400);
  }
  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const PrimaryButton = styled.button`
  min-width: 140px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
  &:disabled {
    background: var(--neutral-300);
    cursor: not-allowed;
  }
`;

const Callout = styled.div<{ tone: 'danger' | 'neutral' }>`
  margin-top: 20px;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 0.875rem;
  background: ${({ tone }) => (tone === 'danger' ? 'var(--danger-50)' : 'var(--neutral-50)')};
  border: 1px solid ${({ tone }) => (tone === 'danger' ? 'var(--danger-500)' : 'var(--border)')};
  color: ${({ tone }) => (tone === 'danger' ? 'var(--danger-500)' : 'var(--neutral-700)')};
`;

const ResultCard = styled.div`
  margin-top: 20px;
  padding: 20px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
  box-sizing: border-box;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const StatusChip = styled.span<{ $available: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 600;
  background: ${({ $available }) => ($available ? 'var(--success-50)' : 'var(--danger-50)')};
  color: ${({ $available }) => ($available ? 'var(--success-700)' : 'var(--danger-500)')};
  border: 1px solid ${({ $available }) => ($available ? 'var(--success-500)' : 'var(--danger-500)')};
`;

const LiveBadge = styled.span<{ $live: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${({ $live }) => ($live ? 'var(--primary-50)' : 'var(--neutral-100)')};
  color: ${({ $live }) => ($live ? 'var(--primary-700)' : 'var(--neutral-500)')};
  border: 1px solid ${({ $live }) => ($live ? 'var(--primary-100)' : 'var(--border)')};
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid var(--border);

  @media only screen and (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: 2px;
  }
`;

const Label = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  align-self: center;
`;

const Value = styled.div`
  font-size: 0.875rem;
  color: var(--mainText);
  word-break: break-word;
`;

const Anchor = styled.a`
  color: var(--primary-700);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export default ConfirmationStatusPage;
